import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getWishlist,
  addToWishlist,
  removeFromWishlist,
  clearWishlist,
  moveItemToCart,
  isUserLoggedIn,
  getLocalWishlist,
  addToLocalWishlist,
  removeFromLocalWishlist,
  clearLocalWishlist,
  moveLocalItemToCart,
  syncWishlistAfterLogin
} from '../utils/Wishlist';
import { toast } from 'react-toastify';
import { getLanguage } from '../utils/Helper';
import ProductsApiService from '../services/ProductsApiService';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(isUserLoggedIn());

  // Load wishlist on mount
  useEffect(() => {
    loadWishlist();
  }, []);

  // Update count whenever items change
  useEffect(() => {
    setWishlistCount(wishlistItems.length);
  }, [wishlistItems]);

  // Listen for wishlist updates
  useEffect(() => {
    const handleWishlistUpdate = () => {
      loadWishlist();
    };

    window.addEventListener('wishlistUpdated', handleWishlistUpdate);

    return () => {
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
    };
  }, []);

  // Load wishlist
  const loadWishlist = useCallback(async () => {
    setIsLoading(true);
    try {
      const currentlyLoggedIn = isUserLoggedIn();
      setIsLoggedIn(currentlyLoggedIn);

      if (currentlyLoggedIn) {
        // Load from API
        const result = await getWishlist();
        if (result.success && result.data) {
          setWishlistItems(result.data.items || []);
        } else {
          setWishlistItems([]);
        }
      } else {
        // Load from localStorage
        const localWishlist = getLocalWishlist();
        
        if (localWishlist.length > 0) {
          // Get variant IDs
          const variantIds = localWishlist.map(item => item.productVariantId);
          
          // Fetch variant details
          const variantsResult = await ProductsApiService.getVariantsByIds(variantIds);
          
          if (variantsResult.success && variantsResult.data) {
            // Map to wishlist items format
            const items = variantsResult.data.map((product, index) => ({
              id: localWishlist[index].productVariantId,
              product: product,
              addedAt: localWishlist[index].addedAt
            }));
            setWishlistItems(items);
          } else {
            setWishlistItems([]);
          }
        } else {
          setWishlistItems([]);
        }
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
      setWishlistItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Add to wishlist
  const addToWishlistHandler = useCallback(async (productVariantId) => {
    const lang = getLanguage();
    
    try {
      let result;
      
      if (isLoggedIn) {
        result = await addToWishlist(productVariantId);
      } else {
        result = addToLocalWishlist(productVariantId);
      }

      if (result.success) {
        // Reload wishlist
        await loadWishlist();
        
        toast.success(
          lang === 'EN' 
            ? '❤️ Added to wishlist!' 
            : '❤️ تمت الإضافة للمفضلة!',
          {
            position: lang === 'EN' ? 'top-right' : 'top-left',
            autoClose: 2000,
          }
        );
        
        return { success: true };
      } else {
        toast.error(
          result.message || (lang === 'EN' 
            ? 'Error adding to wishlist' 
            : 'خطأ في إضافة المنتج للمفضلة'),
          {
            position: lang === 'EN' ? 'top-right' : 'top-left',
          }
        );
        
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      toast.error(
        lang === 'EN' 
          ? 'Error adding to wishlist' 
          : 'خطأ في إضافة المنتج للمفضلة',
        {
          position: lang === 'EN' ? 'top-right' : 'top-left',
        }
      );
      
      return { success: false, message: error.message };
    }
  }, [isLoggedIn, loadWishlist]);

  // Remove from wishlist
  const removeFromWishlistHandler = useCallback(async (itemIdOrVariantId) => {
    const lang = getLanguage();
    
    try {
      let result;
      
      if (isLoggedIn) {
        result = await removeFromWishlist(itemIdOrVariantId);
      } else {
        result = removeFromLocalWishlist(itemIdOrVariantId);
      }

      if (result.success) {
        // Immediately update UI
        setWishlistItems(prev => prev.filter(item => item.id !== itemIdOrVariantId));
        
        // toast.success(
        //   lang === 'EN' 
        //     ? 'Removed from wishlist' 
        //     : 'تم الحذف من المفضلة',
        //   {
        //     position: lang === 'EN' ? 'top-right' : 'top-left',
        //     autoClose: 2000,
        //   }
        // );
        
        return { success: true };
      } else {
        toast.error(
          result.message || (lang === 'EN' 
            ? 'Error removing from wishlist' 
            : 'خطأ في حذف المنتج من المفضلة'),
          {
            position: lang === 'EN' ? 'top-right' : 'top-left',
          }
        );
        
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      toast.error(
        lang === 'EN' 
          ? 'Error removing from wishlist' 
          : 'خطأ في حذف المنتج من المفضلة',
        {
          position: lang === 'EN' ? 'top-right' : 'top-left',
        }
      );
      
      return { success: false, message: error.message };
    }
  }, [isLoggedIn]);

  // Clear wishlist
  const clearWishlistHandler = useCallback(async () => {
    const lang = getLanguage();
    const confirmMessage = lang === 'EN' 
      ? 'Are you sure you want to clear your wishlist?' 
      : 'هل أنت متأكد من حذف جميع المنتجات من قائمة المفضلة؟';
    
    if (!window.confirm(confirmMessage)) {
      return { success: false, cancelled: true };
    }

    try {
      let result;
      
      if (isLoggedIn) {
        result = await clearWishlist();
      } else {
        result = clearLocalWishlist();
      }

      if (result.success) {
        // Immediately update UI
        setWishlistItems([]);
        
        toast.success(
          lang === 'EN' 
            ? 'Wishlist cleared' 
            : 'تم حذف قائمة المفضلة',
          {
            position: lang === 'EN' ? 'top-right' : 'top-left',
            autoClose: 2000,
          }
        );
        
        return { success: true };
      } else {
        toast.error(
          result.message || (lang === 'EN' 
            ? 'Error clearing wishlist' 
            : 'خطأ في حذف قائمة المفضلة'),
          {
            position: lang === 'EN' ? 'top-right' : 'top-left',
          }
        );
        
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Error clearing wishlist:', error);
      toast.error(
        lang === 'EN' 
          ? 'Error clearing wishlist' 
          : 'خطأ في حذف قائمة المفضلة',
        {
          position: lang === 'EN' ? 'top-right' : 'top-left',
        }
      );
      
      return { success: false, message: error.message };
    }
  }, [isLoggedIn]);

  // Move to cart
  const moveToCartHandler = useCallback(async (item, cartAddFunction, setCartFromBackend, transformBackendToFrontend, setCartItems) => {
    const lang = getLanguage();
    
    try {
      let result;
      
      if (isLoggedIn) {
        // Move via API
        result = await moveItemToCart(item.id);
        // Update local state with transformed backend response
        setCartFromBackend(result?.data?.items);
        // Transform backend items to frontend format
        const transformedItems = transformBackendToFrontend(result?.data?.items);
        setCartItems(transformedItems);
      } else {
        // Move in localStorage and add to cart
        result = moveLocalItemToCart(item);
        
        // Find available variant
        const availableVariant = item.product?.variants.find(v => {
          return (v.quantity - v.reservedQuantity > 0) || (v.withoutBoxQuantity - v.withoutBoxReservedQuantity > 0)
        });
        
        if (availableVariant && cartAddFunction) {
          await cartAddFunction(
            item.product,
            availableVariant,
            1,
            availableVariant.quantity ? false : true,
            false
          );
        }
      }

      if (result.success) {
        // Immediately update UI
        setWishlistItems(prev => prev.filter(i => i.id !== item.id));
        
        toast.success(
          lang === 'EN' 
            ? '🛒 Moved to cart!' 
            : '🛒 تم النقل للسلة!',
          {
            position: lang === 'EN' ? 'top-right' : 'top-left',
            autoClose: 2000,
          }
        );
        
        return { success: true, data: result.data };
      } else {
        toast.error(
          result.message || (lang === 'EN' 
            ? 'Error moving to cart' 
            : 'خطأ في نقل المنتج للسلة'),
          {
            position: lang === 'EN' ? 'top-right' : 'top-left',
          }
        );
        
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Error moving to cart:', error);
      toast.error(
        lang === 'EN' 
          ? 'Error moving to cart' 
          : 'خطأ في نقل المنتج للسلة',
        {
          position: lang === 'EN' ? 'top-right' : 'top-left',
        }
      );
      
      return { success: false, message: error.message };
    }
  }, [isLoggedIn]);

  // Check if variant is in wishlist
  const isInWishlist = useCallback((productVariantId) => {
    return wishlistItems.some(item => 
      item.product.variants && 
      item.product.variants.some(variant => variant.id === productVariantId)
    );
  }, [wishlistItems]);

  // Sync wishlist after login
  const syncWishlist = useCallback(async () => {
    const result = await syncWishlistAfterLogin();
    if (result.success) {
      await loadWishlist();
    }
    return result;
  }, [loadWishlist]);

  const value = {
    wishlistItems,
    wishlistCount,
    isLoading,
    isLoggedIn,
    addToWishlist: addToWishlistHandler,
    removeFromWishlist: removeFromWishlistHandler,
    clearWishlist: clearWishlistHandler,
    moveToCart: moveToCartHandler,
    isInWishlist,
    loadWishlist,
    syncWishlist,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;